import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Coffee, Heart, Users, ShoppingBag, FileText, 
  Lock, Home, Menu, Youtube, Twitter, MessageCircle 
} from 'lucide-react'; 

import { getApiUrl } from '../config/config'; 
import creatorContentService from '../services/creatorContentService';
import FollowButton from '../components/FollowButton';
import GoldCrown from '../components/GoldCrown';
import toast from 'react-hot-toast';

// --- CUSTOM IKONICE ---
const DiscordIcon = () => (<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>);
const TelegramIcon = () => (<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>);

// ==========================================
// STYLED COMPONENTS
// ==========================================
const ProfileContainer = styled.div`min-height: 100vh; background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%); color: #fff;`;
const Header = styled.header`background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%); border-bottom: 1px solid #333; padding: 16px 24px; display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100;`;
const HeaderLeft = styled.div`display: flex; align-items: center; gap: 16px;`;
const HeaderRight = styled.div`display: flex; align-items: center; gap: 16px;`;
const HeaderIcon = styled.button`background: none; border: none; padding: 8px; cursor: pointer; color: #ccc; transition: 0.2s; &:hover { color: #e0c16f; }`;

const ProfileBanner = styled.div`background: #111; height: 200px; position: relative; background-size: cover; background-position: center;`;
const ProfileInfo = styled.div`max-width: 1200px; margin: -60px auto 0; padding: 0 24px; position: relative; z-index: 10; @media(max-width:600px){padding:0 16px;margin-top:-48px;}`;
const ProfileAvatar = styled.div`width: 120px; height: 120px; background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); border-radius: 50%; border: 4px solid #000; overflow: hidden; display:flex; align-items:center; justify-content:center; color:#000; font-size:2.5rem; font-weight:800; img { width: 100%; height: 100%; object-fit: cover; }`;

const ProfileNameContainer = styled.div`display: flex; align-items: center; gap: 4px; margin-bottom: 8px;`;
const ProfileName = styled.h1`font-size: clamp(2rem, 7vw, 2.5rem); font-weight: 800; text-shadow: 0 2px 4px rgba(0,0,0,0.5); overflow-wrap:anywhere;`;

const ActionButtons = styled.div`display: flex; gap: 12px; margin-bottom: 32px; flex-wrap: wrap; @media (max-width: 768px) { flex-direction: column; width: 100%; }`;
const ActionButton = styled.button`display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px 24px; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; transition: 0.3s; &.primary { background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000; } &.secondary { background: transparent; color: #fff; border: 2px solid #e0c16f; }`;

const TabContainer = styled.div`max-width: 1200px; margin: 0 auto 24px; padding: 0 24px; display: flex; gap: 32px; border-bottom: 1px solid #333; overflow-x: auto;`;
const TabBtn = styled.button`background: none; border: none; padding: 12px 0; color: ${props => props.active ? '#e0c16f' : '#888'}; font-weight: bold; font-size: 1rem; cursor: pointer; border-bottom: 2px solid ${props => props.active ? '#e0c16f' : 'transparent'}; text-transform: uppercase; white-space: nowrap;`;

const ContentGrid = styled.div`max-width: 1200px; margin: 0 auto; padding: 0 24px 60px; display: grid; grid-template-columns: ${props => props.hasSidebar ? '1fr 380px' : '1fr'}; gap: 48px; @media (max-width: 992px) { grid-template-columns: 1fr; }`;
const ContentSection = styled.div`background: #111; border-radius: 12px; padding: 24px; border: 1px solid #333; margin-bottom: 32px; @media(max-width:600px){padding:18px;margin-bottom:20px;}`;
const SectionTitle = styled.h3`font-size: 1.25rem; font-weight: 700; color: #fff; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; border-bottom: 1px solid #333; padding-bottom: 12px;`;
const TipButton = styled.button`background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000; border: none; padding: 14px 24px; border-radius: 8px; font-weight: bold; cursor: pointer; transition: 0.2s; &:hover { transform: translateY(-2px); }`;

// VRAĆENE STYLED KOMPONENTE ZA QUICK SUPPORT I MODAL
const SupportForm = styled.div`display: flex; flex-direction: column; gap: 16px;`;
const AmountButton = styled.button`padding: 8px 16px; border: 1px solid #333; background: ${props => props.selected ? 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)' : 'transparent'}; color: ${props => props.selected ? '#000' : '#fff'}; border-radius: 6px; cursor: pointer; font-weight: 500; flex: 1; transition: 0.2s;`;
const ModalOverlay = styled.div`position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.85); backdrop-filter: blur(5px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px;`;
const ModalContent = styled.div`background: #111; border: 1px solid #333; border-radius: 16px; padding: 32px; max-width: 400px; width: 100%; text-align: center; position: relative;`;
const PayButton = styled.button`width: 100%; padding: 14px; margin-bottom: 12px; border-radius: 8px; font-weight: bold; cursor: pointer; border: none; transition: 0.2s; &.stripe { background: #635bff; color: white; } &.paypal { background: #ffc439; color: #000; } &.crypto { background: #0052FF; color: white; } &.cashapp{background:#00d64f;color:#000;} &:hover:not(:disabled){ opacity: 0.9; transform: scale(1.02); } &:disabled{opacity:.45;cursor:not-allowed;}`;

const CreatorProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); 
  const [tiers, setTiers] = useState([]);

  // STATEOVI ZA QUICK SUPPORT
  const [selectedAmount, setSelectedAmount] = useState('5');
  const [customAmount, setCustomAmount] = useState('5');
  const [checkoutItem, setCheckoutItem] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState({stripe:true, paypal:true, cashapp:false, crypto:true});

  useEffect(() => { loadRealCreatorData(); }, [username]);

  const loadRealCreatorData = async () => {
    try {
      setLoading(true);
      const baseUrl = getApiUrl(); 
      const userRes = await fetch(`${baseUrl}/users/public/${username}`);
      if (!userRes.ok) throw new Error('Creator not found');
      const profileData = await userRes.json();
      const cId = profileData.creator_id;

      const [posts, shop, tRes] = await Promise.all([
        cId ? creatorContentService.getFeedPosts(cId).catch(() => []) : [], 
        cId ? creatorContentService.getShopItems(cId).catch(() => []) : [],
        cId ? fetch(`${baseUrl}/subscriptions/plans/creator/${cId}`).then(r => r.ok ? r.json() : {plans:[]}) : {plans:[]}
      ]);

      setCreator({
        id: cId, user_id: profileData.id, username: profileData.username, displayName: profileData.display_name || profileData.username,
        is_gold: Boolean(profileData.is_gold), avatar: profileData.profile_image ? creatorContentService.getImageUrl(profileData.profile_image) : null,
        banner: profileData.banner_image ? creatorContentService.getImageUrl(profileData.banner_image) : null, followers: profileData.followers_count || 0,
        posts, products: shop, about: { bio: profileData.bio || 'No bio yet.', socialLinks: typeof profileData.social_links === 'string' ? JSON.parse(profileData.social_links || '{}') : (profileData.social_links || {}) }
      });
      setTiers(tRes.plans || []);
      fetch(`${baseUrl}/payments/methods`).then(r=>r.json()).then(setPaymentMethods).catch(()=>{});
    } catch (err) { console.error(err); setCreator(null); } finally { setLoading(false); }
  };

  const handleCheckout = async (method) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return alert('Please login first.');
      const amount = Number(String(checkoutItem?.price || '').replace(/[^0-9.]/g, ''));
      let endpoint = '/payments/create-checkout-session';
      let body = { amount, name: checkoutItem?.name, creatorId: creator.id, creatorUsername: creator.username, type: checkoutItem?.kind || (checkoutItem?.id === 'donation' ? 'donation' : 'shop_purchase'), referenceId: checkoutItem?.id };
      if (method === 'crypto') endpoint = '/payments/create-coinbase-charge';
      if (method === 'paypal') {
        endpoint = '/payments/paypal/create-order';
        body = { amount, currency: 'USD', description: `${checkoutItem?.name} on BUCX`, creatorId: creator.id, type: body.type, referenceId: checkoutItem?.id };
      }
      const res = await fetch(`${getApiUrl()}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      const url = data.url || data.order?.approval_url;
      if (!res.ok || !url) throw new Error(data.error || 'This payment method is not configured yet.');
      window.location.href = url;
    } catch (e) { toast.error(e.message || 'Payment failed.'); }
  };

  const AboutWidget = () => (
    <ContentSection>
      <SectionTitle><Users size={20} color="#e0c16f" /> About Creator</SectionTitle>
      <p style={{color: '#ccc', marginBottom: '15px'}}>{creator.about.bio}</p>
      <div style={{ display: 'flex', gap: '12px' }}>
          {creator.about.socialLinks.twitter && <a href={creator.about.socialLinks.twitter} target="_blank" rel="noreferrer"><Twitter size={20} color="#e0c16f"/></a>}
          {creator.about.socialLinks.youtube && <a href={creator.about.socialLinks.youtube} target="_blank" rel="noreferrer"><Youtube size={20} color="#e0c16f"/></a>}
          {creator.about.socialLinks.discord && <a href={creator.about.socialLinks.discord} target="_blank" rel="noreferrer"><DiscordIcon /></a>}
          {creator.about.socialLinks.telegram && <a href={creator.about.socialLinks.telegram} target="_blank" rel="noreferrer"><TelegramIcon /></a>}
      </div>
    </ContentSection>
  );

  const MembershipWidget = () => (
    <ContentSection>
      <SectionTitle><Lock size={20} color="#e0c16f" /> Memberships</SectionTitle>
      {tiers.length === 0 ? <p style={{color:'#777'}}>This creator has not published a membership yet.</p> : <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:'20px'}}>
        {tiers.map(t => (
          <div key={t.id} style={{background:'#000', border:'1px solid #333', padding:'20px', borderRadius:'12px'}}>
            <h4 style={{color:'#e0c16f', marginBottom: '5px', fontSize:'1.1rem'}}>{t.name}</h4>
            <div style={{fontWeight:'bold', marginBottom: '10px', fontSize:'1.2rem'}}>${t.price}/mo</div>
            <TipButton style={{width:'100%', padding: '12px'}} onClick={() => { setCheckoutItem({...t, kind:'membership', price:`$${t.price}`}); setIsPaymentModalOpen(true); }}>Join Now</TipButton>
          </div>
        ))}
      </div>}
    </ContentSection>
  );

  const ShopWidget = () => (
    <ContentSection>
      <SectionTitle><ShoppingBag size={20} color="#e0c16f" /> Shop</SectionTitle>
      {creator.products.length === 0 ? <p style={{color:'#777'}}>This creator’s shop is empty right now.</p> : <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px'}}>
        {creator.products.map(p => (
          <div key={p.id} style={{background: '#000', border: '1px solid #333', padding: '20px', borderRadius: '16px', textAlign: 'center', display:'flex', flexDirection:'column'}}>
            <div style={{width: '100%', aspectRatio: '1', background: '#1a1a1a', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4rem', marginBottom: '20px'}}>
              {p.image_url ? <img src={creatorContentService.getImageUrl(p.image_url)} alt={p.name} style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:'12px'}}/> : '🎁'}
            </div>
            <h4 style={{marginBottom: '10px', fontSize: '1.2rem'}}>{p.name}</h4>
            <div style={{color: '#e0c16f', fontWeight: 'bold', marginBottom: '20px', fontSize: '1.2rem'}}>${Number(p.price).toFixed(2)}</div>
            <TipButton style={{marginTop: 'auto', padding: '12px'}} onClick={() => { setCheckoutItem(p); setIsPaymentModalOpen(true); }}>Buy Now</TipButton>
          </div>
        ))}
      </div>}
    </ContentSection>
  );

  const PostsWidget = () => (
    <ContentSection>
      <SectionTitle><FileText size={20} color="#e0c16f" /> Latest Feed</SectionTitle>
      {creator.posts.length === 0 ? <p style={{color:'#666'}}>No posts yet.</p> : creator.posts.map(p => (
        <div 
          key={p.id} 
          onClick={() => navigate(`/${username}/post/${p.id}`)}
          style={{background: '#000', border: '1px solid #333', borderRadius: '16px', padding: '24px', marginBottom: '20px', cursor: 'pointer', transition: '0.2s'}}
          onMouseOver={(e) => e.currentTarget.style.borderColor = '#e0c16f'}
          onMouseOut={(e) => e.currentTarget.style.borderColor = '#333'}
        >
          <h4 style={{marginBottom: '12px', fontSize: '1.3rem'}}>{p.title}</h4>
          <p style={{color: '#aaa', fontSize: '1rem', marginBottom: '20px', lineHeight:'1.5'}}>{p.content}</p>
          <div style={{display: 'flex', gap: '20px', color: '#888', borderTop: '1px solid #222', paddingTop: '15px'}}>
             <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}><Heart size={18} /> {Number(p.likes_count ?? p.likes ?? 0)}</span>
             <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}><MessageCircle size={18} /> {Number(p.comments_count ?? p.comments ?? 0)}</span>
          </div>
        </div>
      ))}
    </ContentSection>
  );

  if (loading) return <div style={{textAlign:'center', color:'#ccc', marginTop:'100px'}}>Loading...</div>;
  if (!creator || !creator.id) return <div style={{minHeight:'70vh', textAlign:'center', color:'#ccc', paddingTop:'100px'}}>Creator not found.</div>;

  return (
    <ProfileContainer>
      <Header>
        <HeaderLeft><Coffee size={24} color="#e0c16f" /></HeaderLeft>
        <HeaderRight><HeaderIcon onClick={() => navigate('/')}><Home size={20}/></HeaderIcon><HeaderIcon><Menu size={20}/></HeaderIcon></HeaderRight>
      </Header>
      
      <ProfileBanner style={{ backgroundImage: `url(${creator.banner})` }} />
      
      <ProfileInfo>
        <ProfileAvatar>{creator.avatar ? <img src={creator.avatar} alt={`${creator.displayName} avatar`} /> : (creator.displayName || creator.username || '?').charAt(0).toUpperCase()}</ProfileAvatar>
        <ProfileNameContainer>
          <ProfileName>{creator.displayName}</ProfileName>
          {creator.is_gold && <GoldCrown />}
        </ProfileNameContainer>
        <div style={{color: '#e0c16f', marginBottom: '24px'}}>{creator.followers} Followers</div>
        <ActionButtons>
          <ActionButton className="primary" onClick={() => { setCheckoutItem({id:'donation', name:'Support', price:`$${customAmount}`}); setIsPaymentModalOpen(true); }}><Heart size={16} /> Support</ActionButton>
          <ActionButton className="secondary" onClick={() => navigate(`/messages/${creator.user_id}`, {state:{username:creator.displayName}})}><MessageCircle size={16} /> Message</ActionButton>
          <FollowButton creatorId={creator.id} />
        </ActionButtons>
      </ProfileInfo>

      <TabContainer>
        <TabBtn active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>Overview</TabBtn>
        <TabBtn active={activeTab === 'memberships'} onClick={() => setActiveTab('memberships')}>Memberships</TabBtn>
        <TabBtn active={activeTab === 'posts'} onClick={() => setActiveTab('posts')}>Posts</TabBtn>
        <TabBtn active={activeTab === 'shop'} onClick={() => setActiveTab('shop')}>Shop</TabBtn>
      </TabContainer>

      <ContentGrid hasSidebar={activeTab === 'overview'}>
        <div>
          {activeTab === 'overview' && (
            <>
              <AboutWidget />
              <MembershipWidget />
              <ShopWidget />
              <PostsWidget />
            </>
          )}
          {activeTab === 'memberships' && <MembershipWidget />}
          {activeTab === 'posts' && <PostsWidget />}
          {activeTab === 'shop' && <ShopWidget />}
        </div>
        
        {activeTab === 'overview' && (
          <div style={{position:'sticky', top:'100px', height:'fit-content'}}>
            <ContentSection>
              <SectionTitle><Heart size={20} color="#e0c16f" /> Quick Support</SectionTitle>
              <SupportForm>
                <div style={{display:'flex', gap:'8px'}}>
                  {['5','15','25'].map(a => (
                    <AmountButton key={a} selected={selectedAmount === a} onClick={() => {setSelectedAmount(a); setCustomAmount(a);}}>${a}</AmountButton>
                  ))}
                </div>
                <input 
                  type="number" 
                  value={customAmount} 
                  onChange={(e) => setCustomAmount(e.target.value)} 
                  placeholder="Other amount" 
                  style={{padding:'12px', background:'#000', border:'1px solid #333', color:'#fff', borderRadius:'8px', outline:'none'}} 
                />
                <TipButton style={{width:'100%'}} onClick={() => { setCheckoutItem({id:'donation', name:'Donation', price:`$${customAmount}`}); setIsPaymentModalOpen(true); }}>
                  Donate ${customAmount || '0'}
                </TipButton>
              </SupportForm>
            </ContentSection>
          </div>
        )}
      </ContentGrid>

      {/* PAYMENT MODAL (Stripe, PayPal, Crypto) */}
      {isPaymentModalOpen && (
        <ModalOverlay onClick={() => setIsPaymentModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <button style={{position:'absolute', top:'15px', right:'15px', background:'none', border:'none', color:'#888', cursor:'pointer', fontSize:'1.2rem'}} onClick={() => setIsPaymentModalOpen(false)}>✖</button>
            <h3 style={{marginBottom:'10px', color:'#fff'}}>{checkoutItem?.name}</h3>
            <p style={{fontSize:'1.5rem', color:'#e0c16f', fontWeight:'bold', marginBottom:'20px'}}>{checkoutItem?.price}</p>
            
            <PayButton className="stripe" disabled={!paymentMethods.stripe} onClick={() => handleCheckout('stripe')}>💳 Credit / debit card {!paymentMethods.stripe && '(setup required)'}</PayButton>
            {checkoutItem?.kind !== 'membership' && <PayButton className="paypal" disabled={!paymentMethods.paypal} onClick={() => handleCheckout('paypal')}>🅿️ PayPal {!paymentMethods.paypal && '(setup required)'}</PayButton>}
            {checkoutItem?.kind !== 'membership' && <PayButton className="crypto" disabled={!paymentMethods.crypto} onClick={() => handleCheckout('crypto')}>₿ Crypto {!paymentMethods.crypto && '(setup required)'}</PayButton>}
            {paymentMethods.cashapp && <PayButton className="cashapp" disabled title="Cash App confirmation opens through the secure Square payment form">Cash App (available at checkout)</PayButton>}
          </ModalContent>
        </ModalOverlay>
      )}

    </ProfileContainer>
  );
};

export default CreatorProfile;
